/**
 * Studio `/studio` is the creator library. New-game and draft-resume actions
 * navigate to dedicated creation pages, leaving the game grid behind.
 *
 * Selecting a game navigates to its dedicated hub at `/studio/g/:id`
 * ({@link StudioGameHub}) — the detail no longer mounts inline. The legacy fork
 * deep link `/studio?game=<id>` redirects there (landing on the Iterate tab so
 * the copy build's tracker opens directly).
 */
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import type { Creation } from '@djgg/creations';
import { useAuth, useServices } from '../services/context.js';
import { useSubscription } from '../hooks/useSubscription.js';
import { StudioGameCard } from '../studio/StudioGameCard.js';
import { Mascot } from '../components/Mascot.js';
import {
  STUDIO_SORTS,
  STUDIO_OWNERSHIP,
  filterOwnership,
  sortStudioGames,
  type StudioSort,
  type StudioOwnership,
} from '../studio/studioList.js';

export function Studio() {
  const services = useServices();
  const { user } = useAuth();
  const navigate = useNavigate();
  const uid = user?.uid ?? '';

  const myGames = useSubscription<Array<Creation & { id: string }>>(
    (cb) => (uid ? services.subscribe.myCreations(uid, cb) : () => {}),
    [],
    [uid],
  );

  const [params] = useSearchParams();
  const [sort, setSort] = useState<StudioSort>('updated');
  const [ownership, setOwnership] = useState<StudioOwnership>('all');
  // Drafts deleted here leave at once, ahead of the list's next refetch.
  const [deletedIds, setDeletedIds] = useState<ReadonlySet<string>>(new Set());
  const games = useMemo(() => myGames.filter((g) => !deletedIds.has(g.id)), [myGames, deletedIds]);

  // Legacy deep link `/studio?game=<id>` (the fork flow) → the game's hub, on
  // the Iterate tab so the copy build's tracker opens directly.
  const legacyGame = params.get('game');
  useEffect(() => {
    if (legacyGame) {
      navigate(`/studio/g/${legacyGame}?tab=iterate`, { replace: true });
    } else if (params.get('new') === '1') {
      navigate('/studio/create', { replace: true });
    }
  }, [legacyGame, navigate, params]);

  const visible = useMemo(
    () => sortStudioGames(filterOwnership(games, uid, ownership), sort),
    [games, uid, ownership, sort],
  );

  return (
    <div className="container" style={{ paddingBottom: 48 }}>
      <div className="gallery__hero gallery__hero--tight">
        <h1 className="gallery__title">Studio</h1>
        {/* myCreations is a MEMBERSHIP query — this grid also lists games the
            user was invited to as an editor, so say so. */}
        <p className="gallery__subtitle">Create and iterate on the games you create or edit.</p>
      </div>

        <section className="create-hero">
          <span className="create-hero__stamp">no code, promise</span>
          <Mascot context="studio-empty" size={190} quip={false} />
          <div className="create-hero__copy">
            <h2 className="create-hero__title">Make a game</h2>
            <p className="create-hero__tagline">Type an idea. Deej builds it with you.</p>
            {/* Preserve the existing automation entry point across the route move. */}
            <button
              className="btn btn--primary create-hero__cta"
              data-testid="studio-new-tab"
              onClick={() => navigate('/studio/create')}
            >
              Start creating
            </button>
          </div>
        </section>
      <section data-testid="studio-mygames">
        <div className="gallery-shelf__head">
          <h2 className="shelftitle shelftitle--paper">🕹️ Your games ({games.length})</h2>
        </div>

        <div className="toolbar studio-toolbar">
          <div className="tabs" role="tablist" aria-label="Sort">
            {STUDIO_SORTS.map(([value, label]) => (
              <button
                key={value}
                role="tab"
                aria-selected={sort === value}
                className={`tab ${sort === value ? 'tab--active' : ''}`}
                onClick={() => setSort(value)}
                data-testid={`studio-sort-${value}`}
              >
                {label}
              </button>
            ))}
          </div>
          <div className="chips" role="group" aria-label="Ownership">
            {STUDIO_OWNERSHIP.map(([value, label]) => (
              <button
                key={value}
                className={`chip ${ownership === value ? 'chip--active' : ''}`}
                aria-pressed={ownership === value}
                onClick={() => setOwnership(value)}
                data-testid={`studio-own-${value}`}
              >
                {label}
              </button>
            ))}
          </div>
        </div>

        {games.length === 0 ? (
          <div className="panel">
            <div className="empty">
              <Mascot context="studio-empty" />
              No games yet — hit “Start creating” above and Deej takes it from there.
            </div>
          </div>
        ) : visible.length === 0 ? (
          <div className="panel">
            <div className="empty">No games match this filter.</div>
          </div>
        ) : (
          <div className="grid studio-grid" data-testid="studio-grid">
            {visible.map((g) => (
              <StudioGameCard
                key={g.id}
                game={g}
                uid={uid}
                onResume={(id) => navigate(`/studio/create/${id}`)}
                onDeleted={(id) => setDeletedIds((prev) => new Set(prev).add(id))}
              />
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
